import type { AdminMinuteEnrollmentList, MinuteEnrollmentStateEnum } from "@sidegigllc/api/client";
import { AdminMinuteProgramEnrollmentsListStateItem } from "@sidegigllc/api/client";

/** The states the Participants tab lists (WORK-7252): everyone who got past the
 * interest stage, including those who have since left.
 *
 * Stated positively rather than as an exclusion, so a state added to the model
 * later does not silently start appearing here. The two that are absent are the
 * pre-review ones — `interest_submitted` is the review queue, which belongs to
 * the Interested Users tab, and `started` is a pro who cleared the eligibility
 * gate but has submitted no answers to review.
 *
 * Sent to the BE as a comma-separated `state` param by both the table query and
 * the Export button, so the CSV always describes the same row set as the table
 * it was clicked from.
 *
 * Doubles as the Status dropdown's options — one list, not two, since the
 * container collapses a full selection back to "all" by its length.
 */
export const PARTICIPANT_STATES = [
  AdminMinuteProgramEnrollmentsListStateItem.approved,
  AdminMinuteProgramEnrollmentsListStateItem.active,
  AdminMinuteProgramEnrollmentsListStateItem.rejected,
  AdminMinuteProgramEnrollmentsListStateItem.withdrawn,
  AdminMinuteProgramEnrollmentsListStateItem.unenrolled,
] as const satisfies ReadonlyArray<MinuteEnrollmentStateEnum>;

export const getProDisplayName = (row: AdminMinuteEnrollmentList) =>
  `${row.pro_first_name} ${row.pro_last_name}`.trim();

export function getRowTintClass(state: MinuteEnrollmentStateEnum): string {
  // The yellow `interest_submitted` tint is gone with WORK-7252 — those rows no
  // longer reach this table. Disabled pros still do, and stay tinted red.
  return state === "unenrolled" ? "bg-error/10" : "";
}

/** Every state the table can render needs a label: the Status chip reads this
 * record, and it is typed `Record<MinuteEnrollmentStateEnum, string>`. Deliberately wider
 * than {@link PARTICIPANT_STATES}. */
export const STATE_FILTER_LABELS: Record<MinuteEnrollmentStateEnum, string> = {
  interest_submitted: "Interest submitted",
  approved: "Approved",
  started: "Started training",
  active: "Active",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
  unenrolled: "Disabled",
};

// Mirrors the BE `unenroll` transition, which accepts only `approved` and
// `active`. The three terminal states the tab now shows 400 on the endpoint,
// so Disable grays out on those rows.
const DISABLE_ELIGIBLE_STATES = new Set<MinuteEnrollmentStateEnum>(["approved", "active"]);
export const canDisable = (state: MinuteEnrollmentStateEnum) => DISABLE_ELIGIBLE_STATES.has(state);
